import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

export { formatBinAddress } from "@/lib/binLevels";

/** A level count of 0 means the level is not used (FIX-318); bins_per_shelf is always ≥ 1. */
export interface StorageLocationRow {
  id: string;
  company_id: string;
  name: string;
  aisles: number;
  bays_per_aisle: number;
  levels_per_bay: number;
  shelves_per_level: number;
  bins_per_shelf: number;
}

export interface StorageBinRow {
  id: string;
  location_id: string;
  /** Stored bin code; screens show this instead of rebuilding it. */
  address: string;
  /** NULL when the location doesn't use this level (FIX-318). */
  aisle: number | null;
  bay: number | null;
  level: number | null;
  shelf: number | null;
  bin: number;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}


export function useStorageLocations() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["storage_locations", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<StorageLocationRow[]> => {
      const { data, error } = await supabase
        .from("storage_locations")
        .select("*")
        .eq("company_id", companyId!)
        .order("created_at");
      if (error) throw error;
      return (data || []) as StorageLocationRow[];
    },
  });
}

export function useStorageBins(locationId?: string) {
  return useQuery({
    queryKey: ["storage_bins", locationId],
    enabled: !!locationId,
    queryFn: async (): Promise<StorageBinRow[]> => {
      const { data, error } = await supabase
        .from("storage_bins")
        .select("*")
        .eq("location_id", locationId!)
        .order("aisle")
        .order("bay")
        .order("level")
        .order("shelf")
        .order("bin");
      if (error) throw error;
      return (data || []) as StorageBinRow[];
    },
  });
}

export function useAllStorageBins() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["storage_bins_all", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<StorageBinRow[]> => {
      const { data, error } = await supabase
        .from("storage_bins")
        .select("*, storage_locations!inner(company_id)")
        .eq("storage_locations.company_id", companyId!)
        .order("address");
      if (error) throw error;
      return (data || []) as unknown as StorageBinRow[];
    },
  });
}

export function useUpdateBinDescription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, description }: { id: string; description: string }) => {
      const value = description.trim();
      const { error } = await supabase
        .from("storage_bins")
        .update({ description: value === "" ? null : value })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage_bins"] });
      queryClient.invalidateQueries({ queryKey: ["storage_bins_all"] });
    },
  });
}

